import { useState, useCallback, useEffect } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";

// Actual API response structure from /groups/current endpoint
interface GroupMember {
	extra_id: number;
	first_name: string;
	last_name: string;
	email: string;
	mobile_number?: string;
	date_of_birth?: string;
	nationality?: string;
	creator_id: number;
	is_owner: boolean;
	role: "creator" | "member";
}

interface Group {
	group_id: number;
	group_name: string;
	description?: string;
	group_code: number;
	extra_id: number; // The creator's extra_id
	role: "creator" | "member"; // User's role in the group
	members?: GroupMember[]; // Array of group members including creator
}

interface UseGetCurrentGroupReturn {
	group: Group | null;
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Custom hook to get the current user's group
 * @returns Current group data, loading state, error state, and refetch function
 */
export function useGetCurrentGroup(): UseGetCurrentGroupReturn {
	const [group, setGroup] = useState<Group | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const fetchGroup = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<Group> = await apiGet<Group>(
				"/groups/current",
				undefined,
				{
					notification: {
						enabled: false,
						showError: false,
						showSuccess: false,
					},
				}
			);

			if (response.success && response.data) {
				setGroup(response.data);
			} else {
				setGroup(null);
				if (response.error && !response.error.includes("not in a group")) {
					setError(response.error);
				}
			}
		} catch (err) {
			if (err instanceof ApiError) {
				// Don't set error for "not in a group" responses
				if (!err.message.includes("not in a group")) {
					setError(err.message);
				}
				setGroup(null);
			} else if (err instanceof Error) {
				setError(err.message);
				setGroup(null);
			} else {
				setError("An unexpected error occurred");
				setGroup(null);
			}
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchGroup();
	}, []);

	return {
		group,
		loading,
		error,
		refetch: fetchGroup,
	};
}
